import React, { useEffect, useState } from 'react';
import { X, Sunrise, Sun, Sunset, Moon, Home, TreePine, Loader2, Save } from 'lucide-react';

/**
 * LightingBriefModal — PM-side editor for the per-shot Lighting Brief.
 *
 * Founder spec (Feb 2026): Lighting artists need 3 things every shot:
 *   1. Time of day  (day / night / dawn / dusk)
 *   2. BG type      (interior / exterior)
 *   3. Free-text PM brief (mood, key direction, colour temp, refs)
 *
 * All three are REQUIRED before save. The modal surfaces existing values
 * via GET /api/shots/{shot_id}/lighting-brief and persists via PUT.
 *
 * Props:
 *   - shotId         : the parent shot id this brief applies to
 *   - parentCode     : "SH_001" — for the title bar
 *   - api            : axios instance with auth headers
 *   - onClose        : () => void
 *   - onSaved        : (brief) => void   — called after a successful save
 */
const TIME_OF_DAY_OPTIONS = [
  { value: 'day',   label: 'Day',   Icon: Sun,     hint: 'Bright daylight, hard key' },
  { value: 'night', label: 'Night', Icon: Moon,    hint: 'Low-key, moonlight or street' },
  { value: 'dawn',  label: 'Dawn',  Icon: Sunrise, hint: 'Cool blue/pink, soft fill' },
  { value: 'dusk',  label: 'Dusk',  Icon: Sunset,  hint: 'Warm amber, golden hour' },
];

const BG_TYPE_OPTIONS = [
  { value: 'interior', label: 'Interior (INT)', Icon: Home,     hint: 'Indoor — practicals matter' },
  { value: 'exterior', label: 'Exterior (EXT)', Icon: TreePine, hint: 'Outdoor — sky + sun' },
];

export default function LightingBriefModal({ shotId, parentCode, api, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [bgType, setBgType] = useState('');
  const [brief, setBrief] = useState('');
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!shotId || !api) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/shots/${shotId}/lighting-brief`);
        if (cancelled) return;
        if (r.data?.exists) {
          setTimeOfDay(r.data.time_of_day || '');
          setBgType(r.data.bg_type || '');
          setBrief(r.data.brief || '');
          setExisting(r.data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load brief');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shotId, api]);

  const canSave = timeOfDay && bgType && brief.trim().length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setError('');
    try {
      const r = await api.put(`/shots/${shotId}/lighting-brief`, {
        time_of_day: timeOfDay,
        bg_type: bgType,
        brief: brief.trim(),
      });
      if (onSaved) onSaved(r.data.brief);
      onClose && onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="lighting-brief-modal"
    >
      <div
        className="bg-zinc-950 border border-violet-500/40 rounded-lg shadow-2xl w-[640px] max-w-[92vw] max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-violet-500/20 bg-gradient-to-r from-violet-950/40 to-zinc-950 flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-violet-400 font-bold">Lighting Brief</p>
            <h2 className="text-sm text-white font-medium mt-0.5">
              {parentCode || shotId} <span className="text-zinc-500 text-[11px] font-normal">· raw material for lighting artist</span>
            </h2>
          </div>
          <button onClick={onClose} data-testid="lighting-brief-close"
            className="text-zinc-500 hover:text-white p-1.5 rounded hover:bg-zinc-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500 text-xs py-12 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading brief…
            </div>
          ) : (
            <>
              {/* Time of day */}
              <section>
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-bold">Time of Day <span className="text-rose-400">*</span></p>
                <div className="grid grid-cols-4 gap-2" data-testid="lighting-brief-time-of-day">
                  {TIME_OF_DAY_OPTIONS.map(({ value, label, Icon, hint }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTimeOfDay(value)}
                      data-testid={`lighting-brief-tod-${value}`}
                      title={hint}
                      className={`flex flex-col items-center gap-1 py-3 rounded border transition-all ${
                        timeOfDay === value
                          ? 'bg-amber-500/15 border-amber-500/50 text-amber-200 shadow-lg shadow-amber-500/10'
                          : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600 text-zinc-400'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[10px] font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </section>

              {/* BG type */}
              <section>
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-bold">Background Type <span className="text-rose-400">*</span></p>
                <div className="grid grid-cols-2 gap-2" data-testid="lighting-brief-bg-type">
                  {BG_TYPE_OPTIONS.map(({ value, label, Icon, hint }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setBgType(value)}
                      data-testid={`lighting-brief-bg-${value}`}
                      title={hint}
                      className={`flex items-center gap-2 px-4 py-3 rounded border transition-all ${
                        bgType === value
                          ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-200 shadow-lg shadow-cyan-500/10'
                          : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600 text-zinc-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-[11px] font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </section>

              {/* Free-text brief */}
              <section>
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-bold">Lighting Brief <span className="text-rose-400">*</span></p>
                <textarea
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="Describe the lighting intent — mood, key direction, colour temperature, practicals, contrast ratio, reference shots. E.g. 'Warm key from west window, cool fill from monitor glow. Match animatic ref. Soft rim on character L silhouette.'"
                  rows={5}
                  data-testid="lighting-brief-text"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-[12px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/60 resize-none"
                />
                <p className="text-[9px] text-zinc-600 mt-1 italic">
                  This is the raw material the lighting artist sees on their My Tasks card. Be specific.
                </p>
              </section>

              {existing && (
                <p className="text-[9px] text-zinc-600 italic">
                  Last updated by <span className="text-zinc-400">{existing.updated_by_name || '—'}</span>
                  {existing.updated_at ? ` · ${new Date(existing.updated_at).toLocaleString()}` : ''}
                </p>
              )}

              {error && (
                <div className="px-3 py-2 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px]">
                  {String(error)}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between gap-3">
          <p className="text-[9px] text-zinc-600">
            All 3 fields are required. Brief saves to the SHOT so every department (Lighting · Comp · FX) sees it.
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="lighting-brief-cancel"
              className="px-3 py-1.5 text-[11px] text-zinc-300 hover:text-white bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={!canSave}
              data-testid="lighting-brief-save"
              className={`px-4 py-1.5 text-[11px] font-medium rounded flex items-center gap-1.5 transition-all ${
                canSave
                  ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
              }`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? 'Saving…' : (existing ? 'Update Brief' : 'Save Brief')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
